import { useState } from "react";

import CategoryModal from "../CategoryEditor/CategoryModal";
import useNotification from "../shared/Notifications/useNotifications";
import { IPartCategory } from "../../models/Category/Category";
import { CategoryOption } from "./CategoryPicker";

/**
 * Opens the usual Add a category form from the middle of the mapping page, without leaving it.
 * Returns:
 *   - start(name, onDone): opens the form with the name filled in; onDone gets the new category.
 *   - withCreated(categories): the category list plus the one just made, until the list itself is
 *     reloaded, so a picker can show it straight away.
 *   - modal: render it once in the component.
 */
export function useNewCategory() {
  const { addNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [done, setDone] = useState<((category: CategoryOption) => void) | null>(null);
  const [created, setCreated] = useState<CategoryOption | null>(null);

  const start = (initialName: string, onDone: (category: CategoryOption) => void) => {
    setName(initialName);
    setDone(() => onDone);
    setOpen(true);
  };

  const onCreated = (category: IPartCategory) => {
    if (!category?.id) return;

    const option = { id: category.id as string, name: category.name };
    setCreated(option);
    done?.(option);

    addNotification({
      variant: "info",
      primaryText: `${category.name} created`,
      secondaryText: "Customers get it with the next category sync. Parts can't be moved into it until then.",
    });
  };

  const withCreated = (categories: CategoryOption[]) =>
    created && !categories.some((c) => c.id === created.id)
      ? [...categories, created].sort((a, b) => a.name.localeCompare(b.name))
      : categories;

  const modal = <CategoryModal open={open} setOpen={setOpen} defaultName={name} onCreated={onCreated} />;

  return { start, withCreated, modal };
}
